import { HOME } from "../../constants/testIds";

// PLP-flavoured product card — inventory-aware chip + dynamic dispatch line.
import { Link } from "react-router-dom";
import { isInStock, isSoldOut, formatDispatchRange } from "../../lib/preorder";
import { formatINR, placeholderGradient, firstLetter } from "../../lib/product";
import Reveal from "../loadout/Reveal";

const BUCKET_MAP = {
  bestseller: { label: "BESTSELLER", bg: "#FF9F1C", fg: "#0A0A0A" },
  "new-arrival": { label: "NEW", bg: "#FFFFFF", fg: "#0A0A0A" },
  drop: { label: "DROP", bg: "#FF9F1C", fg: "#0A0A0A" },
  "staff-pick": { label: "STAFF PICK", bg: "#0A0A0A", fg: "#FFFFFF" },
};

const NEW_WINDOW_MS = 15 * 24 * 60 * 60 * 1000;

function isNewArrival(product) {
  // Automatic NEW badge: product created in Shopify within the last 15 days
  // (same window as the /drops page). createdAt flows from the Storefront
  // API via the backend product payload.
  const t = Date.parse(product?.createdAt || "");
  return Number.isFinite(t) && Date.now() - t < NEW_WINDOW_MS;
}

function EditorialBadge({ product }) {
  const tags = product?.tags || [];
  // Accept both bare form ("bestseller") and prefixed form ("bucket:bestseller").
  for (const t of tags) {
    const raw = String(t).toLowerCase();
    const key = raw.startsWith("bucket:") ? raw.slice(7) : raw;
    const cfg = BUCKET_MAP[key];
    if (cfg) {
      return (
        <span
          className="absolute top-3 right-3 font-mono uppercase tracking-[0.06em] text-[10px] font-semibold px-2 py-1"
          style={{ background: cfg.bg, color: cfg.fg }}
        >
          {cfg.label}
        </span>
      );
    }
  }
  // No editorial bucket — fall back to the automatic 15-day NEW badge.
  if (isNewArrival(product)) {
    const cfg = BUCKET_MAP["new-arrival"];
    return (
      <span
        data-testid="plp-card-new-badge"
        className="absolute top-3 right-3 font-mono uppercase tracking-[0.06em] text-[10px] font-semibold px-2 py-1"
        style={{ background: cfg.bg, color: cfg.fg }}
      >
        {cfg.label}
      </span>
    );
  }
  return null;
}

// Inventory-aware chip.  Different visual contract from
// components/loadout/StatusBadge (which is used by the homepage grid), so we
// keep it local to the PLP/PDP surface.
export function InventoryChip({ inStock, soldOut = false, size = "sm", className = "" }) {
  const label = soldOut ? "SOLD OUT" : inStock ? "IN STOCK" : "PRE-ORDER";
  const styles = soldOut
    ? { background: "#E5E5E5", color: "#6B6B6B", border: "1px solid #BDBDBD" }
    : inStock
    ? { background: "#FF9F1C", color: "#0A0A0A", border: "1px solid #FF9F1C" }
    : { background: "#FFFFFF", color: "#0A0A0A", border: "1px solid #0A0A0A" };
  const sizeCls =
    size === "md" ? "text-[12px] px-3 py-1.5" : "text-[10px] px-2 py-1";
  return (
    <span
      data-testid="inventory-chip"
      data-status={soldOut ? "sold-out" : inStock ? "in-stock" : "pre-order"}
      className={
        "inline-flex items-center font-mono uppercase tracking-[0.06em] font-semibold " +
        sizeCls +
        " " +
        className
      }
      style={styles}
    >
      {label}
    </span>
  );
}

export default function ProductCard({ product, index = 0, collectionHandle = null }) {
  const inStock = isInStock(product);
  const soldOut = isSoldOut(product);
  const dispatch = formatDispatchRange(new Date(), inStock ? "in-stock" : "pre-order");
  const priceStr = formatINR(product.priceRange?.minVariantPrice);
  const image = product.featuredImage || product.images?.[0];
  // Hover swap — show an alternate variant's image (i.e. another colour) if
  // one exists, else the second gallery image. No swap when the product has
  // only one image.
  const altImage = (() => {
    const primaryUrl = image?.url;
    if (!primaryUrl) return null;
    for (const e of product.variants?.edges || []) {
      const vimg = e?.node?.image;
      if (vimg?.url && vimg.url !== primaryUrl) return vimg;
    }
    const second = product.images?.[1];
    return second?.url && second.url !== primaryUrl ? second : null;
  })();
  // PROMPT 3.3M — DEFECT K: on /collections/mice ONLY, apply the mice-specific
  // top-crop fix. Class-scoped by handle; never a global CSS override.
  const isMice = collectionHandle === "mice";

  return (
    <Reveal delay={Math.min(index * 40, 240)}>
      <Link
        to={`/p/${product.handle}`}
        data-testid={HOME.productCard}
        data-collection-handle={collectionHandle || undefined}
        className={
          "group block bg-white text-[#0A0A0A] border border-transparent hover:border-[#FF9F1C] transition-colors duration-300" +
          (isMice ? " plp-card--mice" : "")
        }
      >
        <div className="relative w-full overflow-hidden bg-[#FAFAFA] aspect-[4/3]">
          {image?.url ? (
            <img
              src={image.url}
              alt={image.altText || product.title}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              className={
                isMice
                  ? "plp-card-fig--mice w-full h-full"
                  : "w-full h-full object-contain p-3 md:p-4"
              }
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ background: placeholderGradient(product.handle) }}
            >
              <span className="font-display text-white/70 text-6xl">
                {firstLetter(product.title)}
              </span>
            </div>
          )}
          {altImage && (
            <img
              src={altImage.url}
              alt=""
              aria-hidden="true"
              loading="lazy"
              data-testid="plp-card-alt-image"
              className={
                "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#FAFAFA] " +
                (isMice
                  ? "plp-card-fig--mice w-full h-full"
                  : "w-full h-full object-contain p-3 md:p-4")
              }
            />
          )}
          <EditorialBadge product={product} />
        </div>
        <div className="p-4 space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] truncate">
            {product.vendor || (product.productType || "PRODUCT")}
          </div>
          <h3
            data-testid={HOME.productTitle}
            className="font-sans text-[14px] md:text-[16px] font-semibold leading-snug line-clamp-2 min-h-[42px]"
            title={product.title}
          >
            {product.title}
          </h3>
          <div className="flex items-baseline gap-2">
            <span
              data-testid={HOME.productPrice}
              className="font-mono text-[18px] font-bold tabular-nums text-[#0A0A0A]"
            >
              {priceStr || "—"}
            </span>
          </div>
          {(inStock || soldOut) && (
            <div className="flex items-center gap-2 pt-1">
              <InventoryChip inStock={inStock} soldOut={soldOut} />
            </div>
          )}
          {soldOut ? (
            <div data-testid="plp-card-sold-out-line" className="font-mono text-[11px] uppercase tracking-[0.04em] text-[#8A8A8A]">
              Currently unavailable
            </div>
          ) : (
            <div
              className="font-mono text-[11px] uppercase tracking-[0.04em] tabular-nums"
              style={{ color: inStock ? "var(--dispatch-instock)" : "var(--dispatch-preorder)" }}
            >
              {dispatch}
            </div>
          )}
        </div>
      </Link>
    </Reveal>
  );
}
